/**
 * Timestamp display components.
 *
 * The rule: **a record renders at its clinic.** A consultation at Clifton reads in
 * Clifton's zone wherever the reader sits; when the reader's own clock would say
 * something different, that reading follows in parentheses. A record with no
 * clinic — a note, a message, an invoice — has no place it happened, so it reads
 * in the viewer's frame with nothing to annotate.
 *
 * Date order and 12/24-hour form always follow the viewer, whichever zone the
 * number is in: a UK vet reads a Clifton record as `20:30 at Clifton`, never
 * `8:30 PM`. Those come from the region toggle, because the browser cannot report
 * them reliably (`navigator.language` would hand that vet `7/27/2026`). The
 * viewer's *zone* is their browser's — the toggle is a data filter, not a claim
 * about where they are sitting.
 *
 * `clinicId` is required but nullable, so `null` states "no clinic" explicitly —
 * an earlier shape let an omitted prop mean the same thing as a clinic-less
 * record, and nothing caught the difference. Whether to annotate is decided on
 * the rendered readings, never on zone ids: a reader in Dublin is on London's
 * clock exactly and must not be told a Balham record happened at
 * "15:30 (15:30 your time)".
 *
 * A date annotates only when the calendar *day* differs; a time whenever the
 * clock does, naming the viewer's day as well when that differs. Timeline headers
 * group by the same per-record frame (`recordFrame`), so an entry can never sit
 * under a header naming a different day than the entry itself shows.
 *
 * These are for **interactive staff UI**, where "the viewer" is meaningful.
 * Generated output — a printed history, a customer-portal page, an email, a
 * persisted task title — has no viewer, or has one who isn't the audience, so it
 * must not use these. Those callers use `resolveDateFormat` with an explicit
 * region, usually the owning member's, read off their own payload.
 *
 * The calendar and schedule views do not render through here either, each for a
 * different reason: `appointment-calendar` is scoped to a single clinic and hands
 * that clinic's timezone to FullCalendar; `clinic-coverage-grid` is a day-grid
 * showing no time of day; `staff-rotation-grid` spans all clinics and
 * works in a fixed rota timezone.
 *
 * Surfaces opened from one of those grids — the hover cards and the detail
 * dialogs — apply the same clinic-first rule through `<FormattedGridTimeRange>`,
 * but take the frame as a required prop rather than a `clinicId`: blocked slots
 * are fetched by time range and staff, never filtered by clinic, so a block's own
 * clinic can differ from the calendar's, and a card must agree with the grid it
 * was opened from rather than with the record. Their date rows use
 * `formatInstantDate` with the same frame and no annotation: the time row already
 * names the viewer's day whenever it differs.
 */
import {
  type DateFormat,
  formatCalendarDate,
  formatInstantDate,
  formatTime,
  getClinic,
  REGIONS_BY_ID,
  toWallClockDate,
} from '@snootsvet/shared'

import { useViewerRegion } from '@/frontend/features/region/region-context'

interface TimestampProps {
  /**
   * An instant — a `timestamptz` column or ISO datetime. A `YYYY-MM-DD` calendar
   * date parses as UTC midnight and shifts back a day in negative-offset zones;
   * those go through `<FormattedCalendarDate>` instead.
   */
  value: Date | string
  /**
   * The clinic the record happened at, or `null` for a record that has none.
   *
   * Required, and deliberately: `null` states "no clinic" rather than being spelled
   * the same as forgetting. It decides the zone the primary reading is in, so
   * getting it wrong is a wrong time — which is exactly why it can't be omitted.
   */
  clinicId: string | null
}

/**
 * Whether an instant falls on the same calendar day in two zones. Compares the
 * `YYYY-MM-DD` form rather than a rendered date, which would call the same day
 * different across locales (`25 Jul 2026` vs `Jul 25, 2026`).
 */
function sameCalendarDay(value: Date | string, zoneA: string, zoneB: string): boolean {
  const instant = typeof value === 'string' ? new Date(value) : value
  return toWallClockDate(instant, zoneA) === toWallClockDate(instant, zoneB)
}

/** A timestamp read at its clinic, plus the viewer's own reading when it differs. */
export type ZoneReading = {
  primary: string
  annotation: string | null
}

/**
 * The frame a record renders in: its clinic's zone with the viewer's date order
 * and clock convention, or the viewer's own frame when the record has no clinic.
 *
 * Shared by the components and by timeline grouping, so an entry's reading and
 * the day-group header above it can never resolve the day differently.
 */
export function recordFrame(clinicId: string | null, viewerFmt: DateFormat): DateFormat {
  const clinic = clinicId ? getClinic(clinicId) : null
  return clinic ? { ...viewerFmt, timeZone: clinic.timezone } : viewerFmt
}

/**
 * Reads a timestamp at its clinic and — when the viewer's own clock would say
 * something different — again in the viewer's frame.
 *
 * `clinicId` decides the primary reading's zone; `null` means the record has no
 * clinic, so it reads in the viewer's frame with nothing to annotate. When the two
 * readings differ, the primary names the clinic so the reader knows whose clock
 * they are looking at, and the annotation carries their own.
 *
 * A date annotates only when the calendar *day* differs, not merely the clock: a
 * mid-afternoon Clifton appointment is the same day in London, so repeating the
 * date would be noise. A time annotates whenever the clock reading differs, and
 * names the viewer's day too when that differs. A datetime annotates with the
 * viewer's time alone when the day matches — its date is already in the primary.
 */
export function describeInZones(
  value: Date | string,
  clinicId: string | null,
  viewerFmt: DateFormat,
  part: 'date' | 'time' | 'datetime',
  style: 'short' | 'long' | 'numeric' = 'short'
): ZoneReading {
  const render = (fmt: DateFormat) =>
    part === 'date'
      ? formatInstantDate(value, fmt, style)
      : part === 'time'
        ? formatTime(value, fmt)
        : `${formatInstantDate(value, fmt, style)}, ${formatTime(value, fmt)}`

  const clinic = clinicId ? getClinic(clinicId) : null
  const recordFmt = recordFrame(clinicId, viewerFmt)
  const primary = render(recordFmt)
  if (!clinic) return { primary, annotation: null }

  // Annotate on what the reader would see, not on whether the zone *ids* differ.
  // The viewer's zone comes from their browser, so it need not be one the clinics
  // use: a reader in Dublin or Lisbon is on London's clock exactly, and repeating
  // an identical reading back at them is noise.
  const viewerReading = render(viewerFmt)
  if (viewerReading === primary) return { primary, annotation: null }

  const sameDay = sameCalendarDay(value, recordFmt.timeZone, viewerFmt.timeZone)

  // A same-day datetime needs the date only once — it's already in the primary
  // reading, so repeating it in the annotation is noise. This is the dominant
  // case (one US clinic among seven, hours ending well before the day rolls
  // over), so most annotated datetimes take this branch.
  if (part === 'datetime' && sameDay) {
    return { primary, annotation: `${formatTime(value, viewerFmt)} your time` }
  }

  // A lone time whose day also differs has to name that day, or "20:30 at Clifton
  // (01:30 your time)" reads as the same evening rather than the one after. The
  // datetime form gets this free — its reading already carries a date.
  if (part === 'time' && !sameDay) {
    return {
      primary,
      annotation: `${formatInstantDate(value, viewerFmt)}, ${viewerReading} your time`,
    }
  }

  return { primary, annotation: `${viewerReading} your time` }
}

/**
 * A time range on a clinic-framed grid, read in the grid's own frame.
 *
 * The inverse of `describeInZones`, and the exception to the rule above. A card
 * anchored to a grid slot has to show the same number as the axis beside it, so
 * here the grid's frame is primary and the *viewer's* reading becomes the
 * annotation. Everywhere that isn't anchored to a clinic-local axis, the viewer
 * wins instead.
 *
 * Compares the rendered ranges rather than the zone ids. The viewer's zone comes
 * from their browser, so it need not be one of the clinics' — a reader in Dublin
 * is on London's clock exactly, and an annotation repeating the grid's own numbers
 * back at them is noise.
 */
export function describeTimeRangeOnGrid(
  start: Date | string,
  end: Date | string,
  gridFmt: DateFormat,
  viewerFmt: DateFormat
): ZoneReading {
  const range = (fmt: DateFormat) => `${formatTime(start, fmt)} – ${formatTime(end, fmt)}`
  const primary = range(gridFmt)
  const viewerReading = range(viewerFmt)
  if (viewerReading === primary) return { primary, annotation: null }
  // A range on the grid carries no date, so when the viewer's day differs the
  // annotation has to name it — "01:30 – 02:00 your time" beside a Friday 8:30 PM
  // slot is Saturday for the reader, and nothing else on the surface says so.
  const sameDay = sameCalendarDay(start, gridFmt.timeZone, viewerFmt.timeZone)
  return {
    primary,
    annotation: sameDay
      ? `${viewerReading} your time`
      : `${formatInstantDate(start, viewerFmt)}, ${viewerReading} your time`,
  }
}

/**
 * The viewer's actual timezone, read from the browser, falling back to `fallback`
 * when it reports something `Intl` will not accept.
 *
 * The region toggle deliberately does not decide this. It is a data filter — which
 * region's records am I looking at — not a claim about where the reader is sitting,
 * so a UK vet filtering to US patients is still reading them at a London desk.
 *
 * Validates by construction rather than against a list: if `Intl` accepts the zone
 * here, every formatter downstream accepts it too.
 */
export function resolveViewerTimeZone(fallback: string): string {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!detected) return fallback
    new Intl.DateTimeFormat('en-GB', { timeZone: detected })
    return detected
  } catch {
    return fallback
  }
}

/**
 * The viewer's own `DateFormat`: zone from their browser, date order and clock
 * convention from their region.
 *
 * The split is deliberate. The browser knows where the reader is and nothing else
 * does; it does not know they read dates as `27 Jul 2026`, and `navigator.language`
 * would hand a UK vet on a US-English browser `7/27/2026`. So the zone comes from
 * the device and the conventions stay with the region.
 *
 * Takes no clinic: this is the viewer's half only. `recordFrame` lays a record's
 * clinic zone over it, and `describeInZones` uses it as-is for the viewer's own
 * reading in the annotation.
 */
export function useViewerFormat(): DateFormat {
  const { region } = useViewerRegion()
  const { timezone, locale, hour12 } = REGIONS_BY_ID[region]
  return { timeZone: resolveViewerTimeZone(timezone), locale, hour12 }
}

/** Renders a reading and, when the viewer's own clock would differ, theirs alongside. */
function Reading({ reading }: { reading: ZoneReading }) {
  if (!reading.annotation) return <>{reading.primary}</>
  return (
    <>
      {reading.primary} <span className="text-muted-foreground">({reading.annotation})</span>
    </>
  )
}

/**
 * A record's date at its clinic — e.g. "24 Jul 2026", or "Friday, 24 Jul
 * 2026" with `style="long"`, for the surfaces where the weekday is what the reader
 * is actually navigating by.
 */
export function FormattedDate({
  value,
  clinicId,
  style = 'short',
}: TimestampProps & { style?: 'short' | 'long' | 'numeric' }) {
  return <Reading reading={describeInZones(value, clinicId, useViewerFormat(), 'date', style)} />
}

/** A record's time at its clinic — e.g. "21:30", or "9:30 PM" for a US viewer. */
export function FormattedTime({ value, clinicId }: TimestampProps) {
  return <Reading reading={describeInZones(value, clinicId, useViewerFormat(), 'time')} />
}

/**
 * A record's date and time as one unit, so a cross-region record carries a single
 * annotation rather than two saying the same thing twice.
 */
export function FormattedDateTime({ value, clinicId }: TimestampProps) {
  return <Reading reading={describeInZones(value, clinicId, useViewerFormat(), 'datetime')} />
}

/**
 * A time range on a clinic-local grid — e.g. "2:00 PM – 2:30 PM (19:00 – 19:30
 * your time)". For hover cards and popovers anchored to a calendar slot, whose
 * number has to match the axis they point at.
 *
 * `fmt` is the grid's own frame, and required: a grid-anchored card has no
 * sensible default, and falling back to the viewer's frame is the bug this
 * exists to prevent.
 */
export function FormattedGridTimeRange({
  start,
  end,
  fmt,
}: {
  start: Date | string
  end: Date | string
  fmt: DateFormat
}) {
  return <Reading reading={describeTimeRangeOnGrid(start, end, fmt, useViewerFormat())} />
}

/**
 * A calendar date — a day rather than a moment, so it has no timezone half at
 * all: a stock expiry or a date of birth names the same day everywhere.
 *
 * Only the locale applies, so these still follow the viewer (`15 Jun 2027` vs
 * `Jun 15, 2027`) — which is why they belong here rather than on bare
 * `formatInstantDate`. Use for `date` columns (`stock.expiryDate`), `z.iso.date()`
 * fields (`task.dueDate`), and day-valued `timestamp` columns like
 * `patient.dateOfBirth`. Never `<FormattedDate>`, which would shift the day.
 */
export function FormattedCalendarDate({
  value,
  style = 'short',
}: {
  value: Date | string
  style?: 'short' | 'long'
}) {
  // Calendar dates have no zone, so only the locale half of the format applies.
  const fmt = useViewerFormat()
  return <>{formatCalendarDate(value, fmt, style)}</>
}
